import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { SortColumn } from 'src/app/models/sortColumn.model';
import { StoreService } from 'src/app/services/infra/store/store.service';
import { ISelectOption } from '../../../selectOptions';
import { FilterTypesEnum } from '../../filterTypes.enum';
import { IPageFilters } from '../../interfaces/pageFilters.interface';
import { ISelectedFilters } from '../../selectedFilters.interface';
import { DateFilter } from '../filtersTypes/dateFilter.model';
import { DynamicMultiSelectFilters } from '../filtersTypes/dynamicFilter.model';
import { MultiSelectFilters } from '../filtersTypes/multiFilter.model';
import { FeatureFlagsService } from 'src/app/services/infra/featureFlags/feature-flags.service';
import { AiAgentStepsHttpService } from 'src/app/services/httpServices/detection/aiAgentStepsHttpService/ai-agent-steps-http.service';
import { FiltersQueryTransformer } from 'src/app/services/filters/filtersQueryTransformer.ts/filtersQueryTransformer';
import { CreationTimeSinceOptions } from 'src/app/models/selectOptions/creationTimeSinceOptions';
import moment from 'moment';
import { StepTypeSelectOptions } from 'src/app/models/selectOptions/stepTypeSelectOptions.model';
import { checkAllGivenIdsExistInOptions, fromIntegrationToSelectOption } from './utils';
import { IntegrationModel } from 'src/app/models/api';
import { AiServiceSelectOptions } from 'src/app/models/selectOptions/aiServiceSelectOptions.mode';
import { clientApplicationSelectOptions } from 'src/app/models/selectOptions/clientApplicationSelectOptions.model';
import { EnterpriseAiDetectionTabsEnum } from 'src/app/views/enterprise-ai/enterprise-ai-detection-tabs-management/enterprise-ai-detection-tabs-enum';
import { AutocompleteSearchResultsV2Model } from 'src/app/models/api/autocompleteSearchResultsV2.model';
import { DisplayCamelCaseStringPipe } from 'src/app/pipes/display-camel-case-string/display-camel-case-string.pipe';
import { agentStepsActivityTableColumns } from 'src/app/models/tableColumns/aiAgentStepActivityTableColumn';
import { ITableColumn } from 'src/app/models/tableColumns';

export class AiActivityFiltersModel implements IPageFilters {
  public onUpdate$: BehaviorSubject<ISelectedFilters>;
  public allOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>([]);
  public typeFilters: MultiSelectFilters;
  public fromDate: DateFilter;
  public toDate: DateFilter;
  public actorFilters: DynamicMultiSelectFilters;
  public threadIdFilters: DynamicMultiSelectFilters;
  public agentFilters: DynamicMultiSelectFilters;
  public environmentFilters: DynamicMultiSelectFilters;
  public creationTimeSinceDate: MultiSelectFilters;
  public integrationFilters: MultiSelectFilters;
  public aiServiceFilters: MultiSelectFilters;
  public clientApplicationFilters: MultiSelectFilters;

  public readonly defaultSortColumn: ITableColumn | undefined = agentStepsActivityTableColumns.find(
    (column) => column.sortKey === 'timestamp'
  );

  public sortedColumn$: BehaviorSubject<SortColumn | undefined> = new BehaviorSubject<SortColumn | undefined>(
    this.defaultSortColumn ? { column: this.defaultSortColumn, isDesc: true } : undefined
  );

  constructor(
    private readonly _router: Router,
    private readonly _store: StoreService,
    private readonly _aiActivityHttpService: AiAgentStepsHttpService,
    public featureFlagsService: FeatureFlagsService,
    private readonly _displayCamelCaseStringPipe: DisplayCamelCaseStringPipe
  ) {
    this._initializeFilters();
    this._integrationsOnUpdate();

    this.onUpdate$ = new BehaviorSubject(this.getSelectedFilters());
  }

  public getSelectedFilters(): ISelectedFilters {
    return {
      fromDate: this.fromDate.getFilterValue(),
      toDate: this.toDate.getFilterValue(),
      type: this.typeFilters.getSelectedFiltersValues(),
      actor: this.actorFilters.getSelectedFiltersValues(),
      threadId: this.threadIdFilters.getSelectedFiltersValues(),
      integration: this.integrationFilters.getSelectedFiltersValues(),
      aiService: this.aiServiceFilters.getSelectedFiltersValues(),
      clientApplication: this.clientApplicationFilters.getSelectedFiltersValues(),
      agent: this.agentFilters.getSelectedFiltersValues(),
      environment: this.environmentFilters.getSelectedFiltersValues(),
    };
  }

  public getAllFiltersOptions(): BehaviorSubject<ISelectOption[]> {
    return this.allOptions$;
  }

  public getAllSelectedOptions(): ISelectOption[] {
    const selectedOptions = [
      ...this.creationTimeSinceDate.getSelectedOptions(),
      ...this.typeFilters.getSelectedOptions(),
      ...this.actorFilters.getSelectedOptions(),
      ...this.threadIdFilters.getSelectedOptions(),
      ...this.integrationFilters.getSelectedOptions(),
      ...this.aiServiceFilters.getSelectedOptions(),
      ...this.clientApplicationFilters.getSelectedOptions(),
      ...this.agentFilters.getSelectedOptions(),
      ...this.environmentFilters.getSelectedOptions(),
    ];

    return selectedOptions;
  }

  public updateFilterFromUserSelection(
    filterToUpdate: FilterTypesEnum | undefined,
    value: ISelectOption[] | Date | boolean | string | undefined
  ) {
    let needUpdate = false;
    if (filterToUpdate) {
      switch (filterToUpdate) {
        case FilterTypesEnum.FromDate:
          this.fromDate.update(value as Date | undefined);
          needUpdate = true;
          break;
        case FilterTypesEnum.ToDate:
          this.toDate.update(value as Date | undefined);
          needUpdate = true;
          break;
      }
    } else {
      if (value instanceof Array) {
        if (value.length == 0) {
          needUpdate = this.clearAllMultiSelectFiltersIfNeeded();
        } else {
          const optionsByCategory: {
            [key: string]: ISelectOption[];
          } = {};
          value.forEach((option) => {
            if (option.filterType) {
              if (option.filterType in optionsByCategory) {
                optionsByCategory[option.filterType].push(option);
              } else {
                optionsByCategory[option.filterType] = [option];
              }
            }
          });
          this._updateAllOptions();

          needUpdate = this.typeFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.StepType] || []) || needUpdate;
          needUpdate = this.actorFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.Actor] || []) || needUpdate;
          needUpdate =
            this.threadIdFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.ThreadId] || []) || needUpdate;
          needUpdate =
            this.integrationFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.Integration] || []) || needUpdate;
          needUpdate =
            this.aiServiceFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.AiService] || []) || needUpdate;
          needUpdate =
            this.clientApplicationFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.ClientApplication] || []) ||
            needUpdate;
          needUpdate =
            this.agentFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.AIAgentName] || []) || needUpdate;
          needUpdate =
            this.environmentFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.Environment] || []) || needUpdate;
        }
      }
    }
    if (needUpdate) {
      const filtersQuery = FiltersQueryTransformer.transformToQuery(this.getSelectedFilters());
      this._router.navigate([], {
        queryParams: {
          tab: EnterpriseAiDetectionTabsEnum.Activity,
          q: filtersQuery,
        },
        queryParamsHandling: 'merge',
      });
      this.onUpdate$.next(this.getSelectedFilters());
    }
  }

  public getFilterOptionsIfExist$(filter: FilterTypesEnum): BehaviorSubject<ISelectOption[]> {
    switch (filter) {
      case FilterTypesEnum.StepType:
        return this.typeFilters.getOptions$();
      case FilterTypesEnum.Actor:
        return this.actorFilters.getOptions$();
      case FilterTypesEnum.ThreadId:
        return this.threadIdFilters.getOptions$();
      case FilterTypesEnum.Integration:
        return this.integrationFilters.getOptions$();
      case FilterTypesEnum.AiService:
        return this.aiServiceFilters.getOptions$();
      case FilterTypesEnum.ClientApplication:
        return this.clientApplicationFilters.getOptions$();
      case FilterTypesEnum.AIAgentName:
        return this.agentFilters.getOptions$();
      case FilterTypesEnum.Environment:
        return this.environmentFilters.getOptions$();

      default:
        throw new Error(`AiActivityFiltersModel getFilterOptionsIfExist: ${filter} not supported`);
    }
  }

  public removeSelectedOptionsIfExist(options: ISelectOption[]): void {
    const optionsIdsByCategory: { [key: string]: Set<string> } = {};
    options.forEach(
      (option) => {
        if (option.filterType) {
          if (option.filterType in optionsIdsByCategory) {
            optionsIdsByCategory[option.filterType].add(option.id);
          } else {
            optionsIdsByCategory[option.filterType] = new Set([option.id]);
          }
        }
      },
      this.getDynamicFilters().forEach((filter) => {
        optionsIdsByCategory[filter] = new Set();
      })
    );

    if (optionsIdsByCategory[FilterTypesEnum.StepType]) {
      this.typeFilters.updateIfNeeded(
        this.typeFilters
          .getSelectedOptions()
          .filter((selectedOption) => !optionsIdsByCategory[FilterTypesEnum.StepType].has(selectedOption.id))
      );
    }

    if (optionsIdsByCategory[FilterTypesEnum.Actor]) {
      this.actorFilters.updateIfNeeded(
        this.actorFilters
          .getSelectedOptions()
          .filter((selectedOption) => !optionsIdsByCategory[FilterTypesEnum.Actor].has(selectedOption.id))
      );
    }
    if (optionsIdsByCategory[FilterTypesEnum.ThreadId]) {
      this.threadIdFilters.updateIfNeeded(
        this.threadIdFilters
          .getSelectedOptions()
          .filter((selectedOption) => !optionsIdsByCategory[FilterTypesEnum.ThreadId].has(selectedOption.id))
      );
    }
    if (optionsIdsByCategory[FilterTypesEnum.Integration]) {
      this.integrationFilters.updateIfNeeded(
        this.integrationFilters
          .getSelectedOptions()
          .filter((selectedOption) => !optionsIdsByCategory[FilterTypesEnum.Integration].has(selectedOption.id))
      );
    }
    if (optionsIdsByCategory[FilterTypesEnum.AiService]) {
      this.aiServiceFilters.updateIfNeeded(
        this.aiServiceFilters
          .getSelectedOptions()
          .filter((selectedOption) => !optionsIdsByCategory[FilterTypesEnum.AiService].has(selectedOption.id))
      );
    }
    if (optionsIdsByCategory[FilterTypesEnum.ClientApplication]) {
      this.clientApplicationFilters.updateIfNeeded(
        this.clientApplicationFilters
          .getSelectedOptions()
          .filter((selectedOption) => !optionsIdsByCategory[FilterTypesEnum.ClientApplication].has(selectedOption.id))
      );
    }

    if (optionsIdsByCategory[FilterTypesEnum.AIAgentName]) {
      this.agentFilters.updateIfNeeded(
        this.agentFilters
          .getSelectedOptions()
          .filter((selectedOption) => !optionsIdsByCategory[FilterTypesEnum.AIAgentName].has(selectedOption.id))
      );
    }

    if (optionsIdsByCategory[FilterTypesEnum.Environment]) {
      this.environmentFilters.updateIfNeeded(
        this.environmentFilters
          .getSelectedOptions()
          .filter((selectedOption) => !optionsIdsByCategory[FilterTypesEnum.Environment].has(selectedOption.id))
      );
    }

    const filtersQuery = FiltersQueryTransformer.transformToQuery(this.getSelectedFilters());
    this._router.navigate([], {
      queryParams: { tab: 'activity', q: filtersQuery },
      queryParamsHandling: 'merge',
    });
    this.onUpdate$.next(this.getSelectedFilters());
  }

  public getFilterSelectedIfExist(filter: FilterTypesEnum): string[] {
    switch (filter) {
      case FilterTypesEnum.StepType:
        return this.typeFilters.getSelectedFiltersIds();
      case FilterTypesEnum.Actor:
        return this.actorFilters.getSelectedFiltersIds();
      case FilterTypesEnum.ThreadId:
        return this.threadIdFilters.getSelectedFiltersIds();
      case FilterTypesEnum.Integration:
        return this.integrationFilters.getSelectedFiltersIds();
      case FilterTypesEnum.AiService:
        return this.aiServiceFilters.getSelectedFiltersIds();
      case FilterTypesEnum.ClientApplication:
        return this.clientApplicationFilters.getSelectedFiltersIds();
      case FilterTypesEnum.AIAgentName:
        return this.agentFilters.getSelectedFiltersIds();
      case FilterTypesEnum.Environment:
        return this.environmentFilters.getSelectedFiltersIds();

      default:
        throw new Error(`AiActivityFiltersModel getFilterSelectedIfExist: ${filter} not supported`);
    }
  }

  public updateFiltersByKeyValue(keyValueFilters: { [key: string]: string[] }, updateQueryParams = false): void {
    let needUpdate = false;

    let values: string[] = [];

    if ('fromDate' in keyValueFilters || this.fromDate.getFilterValue() !== undefined) {
      this.fromDate.update(
        keyValueFilters['fromDate'] && moment(keyValueFilters['fromDate'][0], 'x').isValid()
          ? moment(keyValueFilters['fromDate'][0], 'x').toDate()
          : undefined
      );
      needUpdate = true;
    }

    if ('toDate' in keyValueFilters || this.toDate.getFilterValue() !== undefined) {
      this.toDate.update(
        keyValueFilters['toDate'] && moment(keyValueFilters['toDate'][0], 'x').isValid()
          ? moment(keyValueFilters['toDate'][0], 'x').toDate()
          : undefined
      );
      needUpdate = true;
    }

    values = 'creationTimeSinceDate' in keyValueFilters ? keyValueFilters['creationTimeSinceDate'] : [];
    needUpdate = this.creationTimeSinceDate.updateByValuesIfNeeded(values) || needUpdate;

    values = 'type' in keyValueFilters ? keyValueFilters['type'] : [];
    needUpdate = this.typeFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'integration' in keyValueFilters ? keyValueFilters['integration'] : [];
    needUpdate = this.integrationFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'aiService' in keyValueFilters ? keyValueFilters['aiService'] : [];
    needUpdate = this.aiServiceFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'clientApplication' in keyValueFilters ? keyValueFilters['clientApplication'] : [];
    needUpdate = this.clientApplicationFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'actor' in keyValueFilters ? keyValueFilters['actor'] : [];
    if (values.length > 0 && !checkAllGivenIdsExistInOptions(this.actorFilters.getOptions$().value, values)) {
      this.actorFilters = new DynamicMultiSelectFilters([]);
      this._actorOnUpdate(values);
    }
    needUpdate = this.actorFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'threadId' in keyValueFilters ? keyValueFilters['threadId'] : [];
    if (values.length > 0 && !checkAllGivenIdsExistInOptions(this.threadIdFilters.getOptions$().value, values)) {
      this.threadIdFilters = new DynamicMultiSelectFilters([]);
      this._threadIdOnUpdate(values);
    }
    needUpdate = this.threadIdFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'agent' in keyValueFilters ? keyValueFilters['agent'] : [];
    if (values.length > 0 && !checkAllGivenIdsExistInOptions(this.agentFilters.getOptions$().value, values)) {
      this.agentFilters = new DynamicMultiSelectFilters([]);
      this._agentOnUpdate(values);
    }
    needUpdate = this.agentFilters.updateByValuesIfNeeded(values) || needUpdate;

    values = 'environment' in keyValueFilters ? keyValueFilters['environment'] : [];
    if (values.length > 0 && !checkAllGivenIdsExistInOptions(this.environmentFilters.getOptions$().value, values)) {
      this.environmentFilters = new DynamicMultiSelectFilters([]);
      this._environmentOnUpdate(values);
    }
    needUpdate = this.environmentFilters.updateByValuesIfNeeded(values) || needUpdate;

    if (needUpdate) {
      if (updateQueryParams) {
        const filtersQuery = FiltersQueryTransformer.transformToQuery(this.getSelectedFilters());
        this._router.navigate([], {
          queryParams: { tab: 'activity', q: filtersQuery },
          queryParamsHandling: 'merge',
        });
      }
      this.onUpdate$.next(this.getSelectedFilters());
    }
  }

  public clearAllMultiSelectFiltersIfNeeded(): boolean {
    return (
      this.typeFilters.updateIfNeeded([]) ||
      this.actorFilters.updateIfNeeded([]) ||
      this.threadIdFilters.updateIfNeeded([]) ||
      this.integrationFilters.updateIfNeeded([]) ||
      this.aiServiceFilters.updateIfNeeded([]) ||
      this.clientApplicationFilters.updateIfNeeded([]) ||
      this.agentFilters.updateIfNeeded([]) ||
      this.environmentFilters.updateIfNeeded([])
    );
  }

  public getDynamicFilters(): string[] {
    return [FilterTypesEnum.Actor, FilterTypesEnum.ThreadId, FilterTypesEnum.AIAgentName, FilterTypesEnum.Environment];
  }

  private _initializeFilters() {
    this.fromDate = new DateFilter();
    this.toDate = new DateFilter();
    this.creationTimeSinceDate = new MultiSelectFilters(CreationTimeSinceOptions);
    this.typeFilters = new MultiSelectFilters(StepTypeSelectOptions);
    this.actorFilters = new DynamicMultiSelectFilters([]);
    this.threadIdFilters = new DynamicMultiSelectFilters([]);
    this.integrationFilters = new MultiSelectFilters([]);
    this.aiServiceFilters = new MultiSelectFilters(AiServiceSelectOptions);
    this.clientApplicationFilters = new MultiSelectFilters(clientApplicationSelectOptions);
    this.agentFilters = new DynamicMultiSelectFilters([]);
    this.environmentFilters = new DynamicMultiSelectFilters([]);
    this._updateAllOptions();
  }

  private _threadIdOnUpdate(threadIds: string[]) {
    const threadOptions = threadIds.map(
      (thread) =>
        ({
          id: thread,
          value: thread,
          displayName: thread,
          filterType: FilterTypesEnum.ThreadId,
          categoryName: FilterTypesEnum.ThreadId,
          withoutBeautifyDisplayName: true,
        }) as ISelectOption
    );

    const shouldUpdateSelectedOptions = this.threadIdFilters.updateOptions(threadOptions);
    this._updateAllOptions();
    if (shouldUpdateSelectedOptions) {
      this.onUpdate$.next(this.getSelectedFilters());
    }
  }

  private _actorOnUpdate(actorsIds: string[]) {
    this._fetchAndUpdateFilterOptions(actorsIds, 'mainActorDisplayName', FilterTypesEnum.Actor, this.actorFilters);
  }

  private _agentOnUpdate(agentsIds: string[]) {
    this._fetchAndUpdateFilterOptions(
      agentsIds,
      'aiAgentDisplayName',
      FilterTypesEnum.AIAgentName,
      this.agentFilters,
      (text) => this._displayCamelCaseStringPipe.transform(text)
    );
  }

  private _environmentOnUpdate(environmentsIds: string[]) {
    this._fetchAndUpdateFilterOptions(
      environmentsIds,
      'environment',
      FilterTypesEnum.Environment,
      this.environmentFilters
    );
  }

  private _fetchAndUpdateFilterOptions(
    ids: string[],
    filterName: string,
    filterType: FilterTypesEnum,
    filters: DynamicMultiSelectFilters,
    transformDisplayName?: (text: string) => string
  ) {
    this._aiActivityHttpService
      .getDisplayNameById(filterName, ids)
      .subscribe((autocompleteResults: AutocompleteSearchResultsV2Model[]) => {
        const options: ISelectOption[] = autocompleteResults?.map((result) => {
          return {
            id: result.id,
            value: result.id,
            displayName: transformDisplayName ? transformDisplayName(result.resultText) : result.resultText,
            filterType: filterType,
            categoryName: filterType,
            withoutBeautifyDisplayName: true,
          } as ISelectOption;
        });

        const shouldUpdateSelectedOptions = filters.updateOptions(Object.values(options));

        this._updateAllOptions();
        if (shouldUpdateSelectedOptions) {
          this.onUpdate$.next(this.getSelectedFilters());
        }
      });
  }

  private _updateAllOptions() {
    this.allOptions$.next([
      ...this.typeFilters.getOptions$().value,
      ...this.actorFilters.getOptions$().value,
      ...this.threadIdFilters.getOptions$().value,
      ...this.integrationFilters.getOptions$().value,
      ...this.aiServiceFilters.getOptions$().value,
      ...this.clientApplicationFilters.getOptions$().value,
      ...this.agentFilters.getOptions$().value,
      ...this.environmentFilters.getOptions$().value,
    ]);
  }

  private _integrationsOnUpdate() {
    this._store.integrations$.subscribe((integrations: IntegrationModel[]) => {
      const integrationsPlatformSet: Set<string> = new Set();

      const integrationsOptionsMap: ISelectOption[] = integrations.map((integration) => {
        integrationsPlatformSet.add(integration.platform);
        return fromIntegrationToSelectOption(integration);
      });

      const shouldUpdateSelectedOptions = this.integrationFilters.updateOptions(Object.values(integrationsOptionsMap));
      this._updateAllOptions();
      if (shouldUpdateSelectedOptions) {
        this.onUpdate$.next(this.getSelectedFilters());
      }
    });
  }
}
